import { useCallback, useRef, useState } from "react";
import { cn } from "@web/lib/utils.ts";

/**
 * Collapsible JSON tree for raw payloads.
 * Open/closed is path-keyed at the root so every depth toggles the same way,
 * and reopening a parent restores nested open nodes instead of resetting them.
 */
export function JsonTree({ value, name = "root" }: { value: unknown; name?: string }) {
  const [openPaths, setOpenPaths] = useState(() => new Set<string>([name]));

  const toggle = useCallback((path: string, row: HTMLElement | null) => {
    setOpenPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
    // Keep the clicked row in view — collapsing tall branches otherwise jumps the page.
    if (row) {
      requestAnimationFrame(() => {
        row.scrollIntoView({ block: "nearest", inline: "nearest" });
      });
    }
  }, []);

  return (
    <ul className="font-mono text-xs text-text">
      <JsonNode
        path={name}
        name={name}
        value={value}
        openPaths={openPaths}
        onToggle={toggle}
      />
    </ul>
  );
}

function JsonNode({
  path,
  name,
  value,
  openPaths,
  onToggle,
}: {
  path: string;
  name: string;
  value: unknown;
  openPaths: ReadonlySet<string>;
  onToggle: (path: string, row: HTMLElement | null) => void;
}) {
  const rowRef = useRef<HTMLLIElement>(null);
  const isObj = value !== null && typeof value === "object";
  const isArr = Array.isArray(value);

  if (!isObj) {
    return (
      <li className="py-0.5">
        <span className="text-faint">{name}</span>
        <span className="text-muted">: </span>
        <span className={typeof value === "string" ? "text-accent" : "text-text"}>
          {formatLeaf(value)}
        </span>
      </li>
    );
  }

  const entries = isArr
    ? (value as unknown[]).map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>);

  const open = openPaths.has(path);

  return (
    <li ref={rowRef} className="py-0.5">
      <button
        type="button"
        className="text-left text-muted hover:text-text"
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle(path, rowRef.current);
        }}
      >
        <span className="inline-block w-3" aria-hidden>
          {open ? "▾" : "▸"}
        </span>
        <span className="text-faint">{name}</span>
        <span className="text-muted">
          {isArr ? ` [${entries.length}]` : ` {${entries.length}}`}
        </span>
      </button>
      {open && (
        <ul className={cn("ml-3 border-l border-line pl-2")}>
          {entries.map(([k, v]) => (
            <JsonNode
              key={`${path}.${k}`}
              path={`${path}.${k}`}
              name={k}
              value={v}
              openPaths={openPaths}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function formatLeaf(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return JSON.stringify(value);
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  if (typeof value === "symbol") return value.toString();
  return Object.prototype.toString.call(value);
}
